import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { 
  ArrowLeft, 
  ArrowRight, 
  FileEdit,
  Sparkles,
  Zap,
  Shield,
  Clock,
  Check,
  Wand2,
  Box,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { PersonaPicker } from "@/components/personas";
import type { ModuleType, PrivacyMode, RunContextPayload, RunConfigPayload, MODULE_TYPE_CONFIG } from "@/lib/types";

const WIZARD_STEPS = [
  { id: "module", title: "Module", description: "Choose action" },
  { id: "input", title: "Input", description: "Your requirements" },
  { id: "context", title: "Context", description: "Add context" },
  { id: "settings", title: "Settings", description: "Configure" },
  { id: "review", title: "Review", description: "Start run" },
];

const MODULE_OPTIONS: { type: ModuleType; icon: React.ElementType; title: string; description: string }[] = [
  {
    type: "evaluation",
    icon: FileEdit,
    title: "Evaluate existing requirements",
    description: "Paste requirements you've already written. Reqqy will analyze clarity and suggest improvements.",
  },
  {
    type: "generation",
    icon: Sparkles,
    title: "Generate requirements from scratch",
    description: "Describe what you need in plain language. Reqqy will help you write clear requirements.",
  },
  {
    type: "refinement",
    icon: Wand2,
    title: "Refine & improve requirements",
    description: "Take existing requirements and improve their clarity, completeness, and testability.",
  },
  {
    type: "prototype",
    icon: Box,
    title: "Generate prototype",
    description: "Turn requirements into a prototype specification with architecture and component design.",
  },
];

export default function NewRunWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Map legacy URL params to new module types
  const getInitialModule = (): ModuleType => {
    const urlMode = searchParams.get("mode");
    if (urlMode === "review") return "evaluation";
    if (urlMode === "draft") return "generation";
    const urlModule = searchParams.get("module") as ModuleType;
    if (urlModule && ["generation", "evaluation", "refinement", "prototype"].includes(urlModule)) return urlModule;
    return "evaluation";
  };
  
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [moduleType, setModuleType] = useState<ModuleType>(getInitialModule());
  const [inputText, setInputText] = useState("");
  const [context, setContext] = useState<Omit<RunContextPayload, "personas">>({
    problemStatement: "",
    constraints: "",
    successMetrics: "",
  });
  const [selectedPersonaIds, setSelectedPersonaIds] = useState<string[]>([]);
  const [config, setConfig] = useState<RunConfigPayload>({
    depth: "quick",
    privacyMode: "standard",
  });

  useEffect(() => {
    setModuleType(getInitialModule());
  }, [searchParams]);

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return moduleType !== null;
      case 1:
        return inputText.trim().length > 10;
      case 2:
        return true;
      case 3:
        return config.depth !== null;
      default:
        return true;
    }
  };

  const handleStartRun = async () => {
    setIsSubmitting(true);
    
    try {
      const { createRun, executeRun } = await import("@/lib/reqqyBackend");
      const { toast } = await import("@/hooks/use-toast");

      const inputPayload = moduleType === "generation"
        ? { raw_idea: inputText }
        : { raw_input: inputText };
      
      const created = await createRun({
        project_id: "demo-project",
        module_type: moduleType,
        input_payload: inputPayload,
        context_payload: {
          ...context,
          personas: selectedPersonaIds,
        },
        config_payload: config,
      });

      if (!created?.id) {
        throw new Error("Backend returned a run without an id");
      }

      console.log("Run created:", created.id);

      // Fire-and-forget execution; RunProgress page will poll
      executeRun(created.id).catch(err => console.error("Execute error:", err));

      navigate(`/dashboard/runs/${created.id}`);
    } catch (err) {
      console.error("Failed to create run:", err);
      const { toast } = await import("@/hooks/use-toast");
      toast({
        title: "Run failed to start",
        description: err instanceof Error ? err.message : "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getInputPlaceholder = () => {
    switch (moduleType) {
      case "evaluation":
        return "As a user, I want to be able to checkout quickly so that I can complete my purchase.\n\nThe checkout should be fast and work on mobile.\n\nWe need payment support...";
      case "generation":
        return "I need a feature that allows users to save their favorite products and get notified when they go on sale. It should work across devices and sync in real-time...";
      case "refinement":
        return "Paste your existing requirements here. Reqqy will improve clarity, add missing acceptance criteria, and resolve ambiguities...";
      case "prototype":
        return "Describe the system or feature you want prototyped. Include key user flows, constraints, and expected behavior...";
    }
  };

  const getInputLabel = () => {
    switch (moduleType) {
      case "evaluation": return "Paste your requirements";
      case "generation": return "Describe what you need";
      case "refinement": return "Paste requirements to refine";
      case "prototype": return "Describe the prototype";
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-display font-bold mb-2">What would you like to do?</h2>
              <p className="text-muted-foreground">Choose how Reqqy can help you today</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              {MODULE_OPTIONS.map(({ type, icon: Icon, title, description }) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setModuleType(type)}
                  className={cn(
                    "p-6 rounded-2xl text-left transition-all border-2",
                    moduleType === type
                      ? "border-primary bg-primary/10"
                      : "border-border hover:border-muted-foreground/50 bg-card"
                  )}
                >
                  <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-primary-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{title}</h3>
                  <p className="text-sm text-muted-foreground">{description}</p>
                  {moduleType === type && (
                    <div className="mt-4 flex items-center gap-2 text-primary text-sm font-medium">
                      <Check className="w-4 h-4" />
                      Selected
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <div>
              <Label htmlFor="input" className="text-base font-medium">
                {getInputLabel()}
              </Label>
              <p className="text-sm text-muted-foreground mt-1 mb-3">
                {moduleType === "evaluation" 
                  ? "User stories, PRD excerpts, bullet notes, or messy text — anything you would normally send to engineering."
                  : moduleType === "generation"
                  ? "Describe the feature, problem, or capability you need. Be as detailed or brief as you like."
                  : moduleType === "refinement"
                  ? "Paste the requirements you want Reqqy to refine and improve."
                  : "Describe the system or feature for prototype generation."
                }
              </p>
              <Textarea
                id="input"
                placeholder={getInputPlaceholder()}
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                rows={12}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground mt-2">
                {inputText.length} characters
              </p>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-display font-bold mb-2">Add context (optional)</h2>
              <p className="text-muted-foreground text-sm">
                Help Reqqy understand the bigger picture for better analysis
              </p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="problem">Problem statement</Label>
                <Textarea
                  id="problem"
                  placeholder="What problem are you trying to solve?"
                  value={context.problemStatement as string}
                  onChange={(e) => setContext({ ...context, problemStatement: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="constraints">Constraints</Label>
                <Textarea
                  id="constraints"
                  placeholder="Technical, business, or timeline constraints..."
                  value={context.constraints as string}
                  onChange={(e) => setContext({ ...context, constraints: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="metrics">Success metrics</Label>
                <Textarea
                  id="metrics"
                  placeholder="How will you measure success?"
                  value={context.successMetrics as string}
                  onChange={(e) => setContext({ ...context, successMetrics: e.target.value })}
                  rows={2}
                />
              </div>
            </div>

            <div className="pt-4 border-t border-border">
              <div className="mb-4">
                <h3 className="font-medium">Personas (optional)</h3>
                <p className="text-sm text-muted-foreground">
                  Select personas from your library to provide user context
                </p>
              </div>
              
              <PersonaPicker
                selectedIds={selectedPersonaIds}
                onSelectionChange={setSelectedPersonaIds}
                showOrgTab={true}
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-display font-bold mb-2">Configure your run</h2>
              <p className="text-muted-foreground text-sm">
                Choose analysis depth and privacy settings
              </p>
            </div>

            <div className="space-y-4">
              <Label className="text-base font-medium">Analysis depth</Label>
              <div className="grid md:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setConfig({ ...config, depth: "quick" })}
                  className={cn(
                    "p-4 rounded-xl text-left transition-all border-2",
                    config.depth === "quick"
                      ? "border-primary bg-primary/10"
                      : "border-border hover:border-muted-foreground/50"
                  )}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Zap className="w-5 h-5 text-primary" />
                    <span className="font-medium">Quick</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Fast analysis focusing on major issues. Best for initial passes.
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => setConfig({ ...config, depth: "deep" })}
                  className={cn(
                    "p-4 rounded-xl text-left transition-all border-2",
                    config.depth === "deep"
                      ? "border-primary bg-primary/10"
                      : "border-border hover:border-muted-foreground/50"
                  )}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Sparkles className="w-5 h-5 text-accent" />
                    <span className="font-medium">Deep</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Thorough analysis with comprehensive output. Takes longer.
                  </p>
                </button>
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t border-border">
              <Label className="text-base font-medium">Privacy mode</Label>
              <div className="grid md:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setConfig({ ...config, privacyMode: "standard" })}
                  className={cn(
                    "p-4 rounded-xl text-left transition-all border-2",
                    config.privacyMode === "standard"
                      ? "border-primary bg-primary/10"
                      : "border-border hover:border-muted-foreground/50"
                  )}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Shield className="w-5 h-5 text-primary" />
                    <span className="font-medium">Standard</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Store history for your convenience. Access past runs anytime.
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => setConfig({ ...config, privacyMode: "temporary" })}
                  className={cn(
                    "p-4 rounded-xl text-left transition-all border-2",
                    config.privacyMode === "temporary"
                      ? "border-primary bg-primary/10"
                      : "border-border hover:border-muted-foreground/50"
                  )}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Clock className="w-5 h-5 text-reqqy-warning" />
                    <span className="font-medium">Temporary</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Delete content after session. Only metrics are retained.
                  </p>
                </button>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-display font-bold mb-2">Ready to run</h2>
              <p className="text-muted-foreground text-sm">
                Review your settings and start the analysis
              </p>
            </div>

            <Card className="glass">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Module</span>
                  <Badge variant="outline" className="capitalize">
                    {MODULE_OPTIONS.find(m => m.type === moduleType)?.title || moduleType}
                  </Badge>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Input length</span>
                  <span className="font-medium">{inputText.length} characters</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Context provided</span>
                  <span className="font-medium">
                    {[context.problemStatement, context.constraints, context.successMetrics].filter(Boolean).length} fields
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Personas</span>
                  <span className="font-medium">{selectedPersonaIds.length}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Analysis depth</span>
                  <Badge variant="outline" className="capitalize">
                    {config.depth === "quick" ? "Quick" : "Deep"}
                  </Badge>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-muted-foreground">Privacy mode</span>
                  <Badge variant="outline" className="capitalize">
                    {config.privacyMode as string}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            <div className="p-4 rounded-xl bg-primary/10 border border-primary/30">
              <p className="text-sm text-center">
                Reqqy will {moduleType === "evaluation" ? "evaluate your requirements" : moduleType === "generation" ? "generate requirements" : moduleType === "refinement" ? "refine your requirements" : "generate a prototype"} and provide detailed analysis.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen p-6 md:p-8 lg:p-10">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/dashboard")}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-2xl md:text-3xl font-display font-bold">New Run</h1>
        </div>

        <div className="flex items-center justify-between mb-8 overflow-x-auto pb-2">
          {WIZARD_STEPS.map((step, index) => (
            <div
              key={step.id}
              className={cn(
                "flex items-center gap-2",
                index < WIZARD_STEPS.length - 1 && "flex-1"
              )}
            >
              <div
                className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all flex-shrink-0",
                  index < currentStep
                    ? "gradient-primary text-primary-foreground"
                    : index === currentStep
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground"
                )}
              >
                {index < currentStep ? (
                  <Check className="w-4 h-4" />
                ) : (
                  index + 1
                )}
              </div>
              <div className="hidden md:block">
                <p className={cn(
                  "text-sm font-medium",
                  index <= currentStep ? "text-foreground" : "text-muted-foreground"
                )}>
                  {step.title}
                </p>
              </div>
              {index < WIZARD_STEPS.length - 1 && (
                <div className={cn(
                  "h-px flex-1 mx-3",
                  index < currentStep ? "bg-primary" : "bg-border"
                )} />
              )}
            </div>
          ))}
        </div>

        {renderStepContent()}

        <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
          <Button
            variant="ghost"
            onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
            disabled={currentStep === 0}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          {currentStep < WIZARD_STEPS.length - 1 ? (
            <Button
              onClick={() => setCurrentStep(currentStep + 1)}
              disabled={!canProceed()}
              className="gradient-primary text-primary-foreground"
            >
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleStartRun}
              disabled={isSubmitting}
              className="gradient-primary text-primary-foreground"
            >
              {isSubmitting ? "Starting..." : "Start Run"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
